import { createWebHistory, createRouter } from 'vue-router';
import store from '@/store/store';
import { useCheckPermission } from '@/hooks/userPermissionHook';
import alertMsg from '@/utils/toastUtils';
import notificationListApi from '@/apis/notificationListApi';
import {
  getUrlLang,
  getLangUrl,
  getLang,
  getToBeLang,
} from '@/utils/commonUtils';

/**
 * 사용자가 지정해서 들어간 언어와 URL안의 언어가 다를 때
 * 사용자가 지정 언어로 url을 세팅해서 화면을 재조회한다.
 */
const updatePathLang = (path) => {
  const pathLang = path.split('/')[1]; // url에 들어가있는 언어
  const userLang = getLang(); // 사용자가 지정한언어
  if (pathLang !== userLang) {
    const newPath = path.replace(`/${pathLang}/`, `/${userLang}/`);
    return newPath;
  }
  return;
};

/**
 * 현재 메뉴 세팅
 * @param {*} to
 * @param {*} from
 * @param {*} next
 */
const settingCurrentMenu = (to, from, next) => {
  store.dispatch('commonRefresh/setCurrentMenu', to.meta.menuPath);
};

const TOOLBAR_COUNT_FETCH_INTERVAL_MS = 30_000;
let lastToolbarCountFetchAt = 0;

/**
 * 툴바 미확인 건수 API — 페이지 이동마다 호출하지 않고 일정 간격으로만 갱신
 */
const fetchToolbarCountsIfNeeded = () => {
  const now = Date.now();
  if (now - lastToolbarCountFetchAt < TOOLBAR_COUNT_FETCH_INTERVAL_MS) {
    return;
  }
  lastToolbarCountFetchAt = now;
  getUnconfirmedAlarmCount();
  getUnconfirmedAsReceiptCount();
};

/**
 * 언어 URL 보정이 필요하면 리다이렉트하고, 아니면 next() 호출
 * @returns {boolean} 리다이렉트했으면 true
 */
const tryNavigateWithLangFix = (to, next) => {
  const newPath = updatePathLang(to.path);
  if (newPath) {
    next(newPath);
    return true;
  }
  next();
  return false;
};

/**
 * 로그인 안되어있으면 로그인페이지로 이동
 */
const checkPermission = (to, from, next) => {
  settingCurrentMenu(to, from, next); // 현재 메뉴 세팅

  let token = store.getters['login/getAccessToken'];
  //토큰 정보없으면 로그인페이지로 이동시키기
  if (token === null || token === '' || token === undefined) {
    next('login');
  } else {
    //MQTT 알람목록 스토어정리
    deleteMqttAlarmList();

    if (to.path.split('/')[2] !== store.state.login?.defaultUrl) {
      let permission = null;
      if (to.meta.menuPath) {
        permission = useCheckPermission(to.meta.menuPath);
      } else {
        permission = useCheckPermission(to.path);
      }

      if (store.state.login.userInfo.siteGroupId) {
        if (permission.isUse) {
          if (!tryNavigateWithLangFix(to, next)) {
            fetchToolbarCountsIfNeeded();
          }
        } else {
          next('/forbidden');
        }
      } else {
        next('/forbidden');
      }
    } else {
      if (!tryNavigateWithLangFix(to, next)) {
        fetchToolbarCountsIfNeeded();
      }
    }
  }
};

/**
 * 권한 검증이 필요없는 화면
 * @param {*} to
 * @param {*} from
 * @param {*} next
 */
const permitPermission = (to, from, next) => {
  let token = store.getters['login/getAccessToken'];

  if (token === null || token === '' || token === undefined) {
    next('login');
  } else {
    next();
  }
};

/**
 * 비로그인 사용자 화면
 * @param {*} to
 * @param {*} from
 * @param {*} next
 */
const guestAccessOnly = (to, from, next) => {
  settingCurrentMenu(to, from, next); // 현재 메뉴 세팅
  next();
};

/**
 * 로그인이 되어있는경우 메인화면으로이동
 */
const loginPermission = (to, from, next) => {
  let token = store.getters['login/getAccessToken'];

  if (token === null || token === '' || token === undefined) {
    next();
  } else {
    //로그아웃
    store.dispatch('login/setLogoutData');
    next();
  }
};

const deleteMqttAlarmList = () => {
  let filteredMqttAlarmList = [];
  let mqttAlarmList = store.state.mqttAlarm.mqttAlarmList;
  //console.log("mqttAlarmList",mqttAlarmList)
  // mqttAlarmList 반복
  /*
  mqttAlarmList.forEach(store => {
    // AI알람뺴고 전부 제거
    let filteredAlarms = store.alarms.filter(alarm => alarm.alarmType === 3);

    // alarmType이 3인 것만 남은 store만 추가
    if (filteredAlarms.length > 0) {
        store.alarms = filteredAlarms;  // 필터링된 alarms 배열로 업데이트
        filteredMqttAlarmList.push(store);  // 필터링된 store를 결과 배열에 추가
    }
  });
  */

  store.dispatch('mqttAlarm/setMqttAlarmList', filteredMqttAlarmList);
};

const getUnconfirmedAlarmCount = async () => {
  try {
    //알람내역 총 개수 조회
    const response = await notificationListApi.getAlarmTotalCount({
      userId: store.state.login.id,
      userAuth: store.state.login.userInfo.userAuth,
      startDate: null,
      endDate: null,
      alarmStatus: 1, //미확인
    });

    //총 건수 알람 아이콘에 적용
    if (response) {
      store.dispatch('toolbar/setUnconfirmedAlarmCount', response.data);
    }
  } catch (error) {
    console.error('API 호출 에러:', error); // 에러 발생 시 로그 출력
  }
};
const getUnconfirmedAsReceiptCount = async () => {
  try {
    //AS접수내역 총 개수 조회
    const response = await notificationListApi.getAsReceiptTotalCount({
      requestType: 1, // 온라인 (스퀘어 접수)
      serviceStatus: 1, //미확인
    });

    //총 건수 알람 아이콘에 적용
    if (response) {
      store.dispatch('toolbar/setUnconfirmedAsReceiptCount', response.data);
    }
  } catch (error) {
    console.error('API 호출 에러:', error); // 에러 발생 시 로그 출력
  }
};
const localeCheck = (to, from, next) => {
  // alert('한번만타야해 먼저타야하고')
  const lang = getLang();
  const urlLang = getUrlLang(lang);
  const moveUrl = to.path.split('/').pop();
  const targetUrl = getLangUrl(moveUrl, urlLang);
  return targetUrl;
};

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', redirect: store.state.login?.defaultUrl || 'login' },
    {
      path: '/ko/',
      redirect: store.state.login?.defaultUrl || 'login',
      component: () => import('@/views/ko/DefaultLayout.vue'),
      children: [
        // 로그인 처리된 레이아웃 적용 할 라우터들은 children에 추가
        {
          meta: { menuPath: 'dash' },
          path: 'dash',
          component: () => import('@/views/ko/dash/DashBoard.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'menu1' },
          path: 'menu1',
          component: () => import('@/views/ko/menu1/MenuFirst.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'menu2' },
          path: 'menu2',
          component: () => import('@/views/ko/menu2/MenuSecond.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'sample' },
          path: 'sample',
          component: () => import('@/views/ko/sample/SamplePage.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'searchAdress' },
          path: 'searchAdress',
          component: () => import('@/views/ko/adress/SampleAdress.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'validation' },
          path: 'validation',
          component: () => import('@/views/ko/validation/SampleValidation.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'dash' },
          path: 'dash',
          component: () => import('@/views/ko/dash/DashBoard.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'menu1' },
          path: 'menu1',
          component: () => import('@/views/ko/menu1/MenuFirst.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'menu2' },
          path: 'menu2',
          component: () => import('@/views/ko/menu2/MenuSecond.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'sample' },
          path: 'sample',
          component: () => import('@/views/ko/sample/SamplePage.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'searchAdress' },
          path: 'searchAdress',
          component: () => import('@/views/ko/adress/SampleAdress.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'validation' },
          path: 'validation',
          component: () => import('@/views/ko/validation/SampleValidation.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'dash' },
          path: 'dash',
          component: () => import('@/views/ko/dash/DashBoard.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'menu1' },
          path: 'menu1',
          component: () => import('@/views/ko/menu1/MenuFirst.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'menu2' },
          path: 'menu2',
          component: () => import('@/views/ko/menu2/MenuSecond.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'sample' },
          path: 'sample',
          component: () => import('@/views/ko/sample/SamplePage.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'searchAdress' },
          path: 'searchAdress',
          component: () => import('@/views/ko/adress/SampleAdress.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'validation' },
          path: 'validation',
          component: () => import('@/views/ko/validation/SampleValidation.vue'),
          beforeEnter: checkPermission,
        },

        // meta를 쓰는이유 : boardView , boardWrite 페이지와같이  실제 메뉴관리 페이지에서 메뉴 등록은 하지 않을거지만 해당 페이지는 게시판의 권한을 따라가야함
        // 그렇기때문에 menuPath를 통해 메뉴관리에 등록되는 url로  menuPath를 맞춰주면 권한을 동일시 체크하는 로직을 짤수있다.
        {
          meta: { menuPath: 'board' },
          path: 'board',
          component: () => import('@/views/ko/board/BoardList.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'board' },
          path: 'boardView',
          component: () => import('@/views/ko/board/BoardView.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'board' },
          path: 'boardWrite',
          component: () => import('@/views/ko/board/BoardWrite.vue'),
          beforeEnter: checkPermission,
        },
        // { meta : { menuPath : "mapboxTest"} , path: 'mapboxTest', component: () => import("@/views/ko/mapboxTest/MapboxTest.vue") , beforeEnter: permitPermission},

        /* 사용자정보 */
        {
          meta: { menuPath: 'userMng' },
          path: 'userMng',
          component: () => import('@/views/ko/userMng/UserMng.vue'),
          beforeEnter: checkPermission,
        },
        /* 관리자 */

        {
          meta: { menuPath: 'menuMng' },
          path: 'menuMng',
          component: () => import('@/views/ko/menuMng/MenuMng.vue'),
          beforeEnter: checkPermission,
        },
        /* 권한별 메뉴관리 */
        {
          meta: { menuPath: 'siteGroupMng' },
          path: 'siteGroupMng',
          component: () => import('@/views/ko/siteGroupMng/SiteGroupMng.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'myInfoMng' },
          path: 'myInfoMng',
          component: () => import('@/views/ko/myInfo/MyInfoMng.vue'),
          beforeEnter: permitPermission,
        },
        /** 파일 임시 업로드 - 테스트 */
        // {
        //   meta: { menuPath: 'tempFileUpload' },
        //   path: 'tempFileUpload',
        //   component: () => import('@/views/ko/sample/TempFileUpload'),
        //   beforeEnter: permitPermission,
        // },

        /* 딜러용 대시보드 */
        {
          meta: { menuPath: 'dealDashBoard' },
          path: 'dealDashBoard',
          component: () => import('@/views/ko/dash/DealDashBoard.vue'),
          beforeEnter: checkPermission,
        },

        /** 연구소 대시보드 */
        {
          meta: { menuPath: 'labDashBoard' },
          path: 'labDashBoard',
          component: () => import('@/views/ko/lab/LabDashBoard.vue'),
          beforeEnter: checkPermission,
        },

        /*알람 내역*/
        {
          meta: { menuPath: 'notificationList' },
          path: 'notificationList',
          component: () =>
            import('@/views/ko/notification/NotificationList.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'notificationList' },
          path: 'notiSummary',
          component: () =>
            import('@/views/ko/notification/details/AlarmDetails.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'notiSettings' },
          path: 'notiSettings',
          component: () => import('@/views/ko/notification/NotiSettings.vue'),
          beforeEnter: checkPermission,
        },
        // { meta : { menuPath : "notification"} , path: 'notificationDetail', name:"notificationDetail", component: () => import("@/views/ko/notification/NotificationDetail.vue") , beforeEnter: checkPermission},

        // { meta : { menuPath : "notificationList"} , path: 'notiSettings', name:"notiSettings", component: () => import("@/views/ko/notification/NotiSettings.vue") , beforeEnter: checkPermission},
        {
          meta: { menuPath: 'serviceDashBoard' },
          path: 'serviceDashBoard',
          component: () =>
            import('@/views/ko/serviceDashBoard/ServiceDashBoard.vue'),
          beforeEnter: checkPermission,
        },

        // 20240701 모니터링 router 추가 dj
        {
          meta: { menuPath: 'monitoringRegion' },
          path: 'monitoringRegion',
          component: () =>
            import('@/views/ko/monitoring/region/MonitoringRegion.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'monitoringShop' },
          path: 'monitoringShop',
          component: () =>
            import('@/views/ko/monitoring/store/MonitoringStore.vue'),
          beforeEnter: checkPermission,
        },

        /*원격제어*/
        {
          meta: { menuPath: 'remoteControl' },
          path: 'remoteControl',
          component: () => import('@/views/ko/remotecontrol/RemoteControl.vue'),
          beforeEnter: checkPermission,
        },

        /*서비스*/
        /*AS접수*/
        {
          meta: { menuPath: 'asReceipt' },
          path: 'asReceipt',
          component: () =>
            import(
              '@/views/ko/service/asGeneraInquire/asReceipt/AsReceipt.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*AS접수상세내역*/
        {
          meta: { menuPath: 'asReceipt' },
          path: 'asReceiptDetail',
          component: () =>
            import(
              '@/views/ko/service/asGeneraInquire/asReceipt/AsReceiptDetail.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*일반문의*/
        {
          meta: { menuPath: 'generalInquire' },
          path: 'generalInquire',
          component: () =>
            import(
              '@/views/ko/service/asGeneraInquire/generalInquire/GeneralInquire.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*서비스내역*/
        {
          meta: { menuPath: 'serviceHistory' },
          path: 'serviceHistory',
          component: () =>
            import(
              '@/views/ko/service/serviceManage/serviceHistory/ServiceHistory.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*서비스진행/처리정보*/
        {
          meta: { menuPath: 'serviceHistory' },
          path: 'serviceHistoryDetail',
          component: () =>
            import(
              '@/views/ko/service/serviceManage/serviceHistory/ServiceHistoryDetail.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*처리가이드검색*/
        {
          meta: { menuPath: 'processGuide' },
          path: 'processGuide',
          component: () =>
            import(
              '@/views/ko/service/processGuide/processGuideSearch/ProcessGuide.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*부품재고현황*/
        {
          meta: { menuPath: 'partsInv' },
          path: 'partsInv',
          component: () =>
            import('@/views/ko/service/partsInfo/partsInv/PartsInv.vue'),
          beforeEnter: checkPermission,
        },
        /*부품주문내역*/
        {
          meta: { menuPath: 'partsOrder' },
          path: 'partsOrder',
          component: () =>
            import('@/views/ko/service/partsInfo/partsOrder/PartsOrder.vue'),
          beforeEnter: checkPermission,
        },
        /*부품주문내역*/
        {
          meta: { menuPath: 'partsOrderUpdate' },
          path: 'partsOrderUpdate',
          component: () =>
            import(
              '@/views/ko/service/partsInfo/partsOrder/PartsOrderUpdate.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*부품주문내역*/
        {
          meta: { menuPath: 'partsOrderHistory' },
          path: 'partsOrderHistory',
          component: () =>
            import(
              '@/views/ko/service/partsInfo/partsOrder/PartsOrderHistory.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*부품주문내역*/
        {
          meta: { menuPath: 'partsOrderApproval' },
          path: 'partsOrderApproval',
          component: () =>
            import(
              '@/views/ko/service/partsInfo/partsOrder/partsOrderApproval.vue'
            ),
          beforeEnter: checkPermission,
        },

        /*분석*/
        /*제품분석*/
        {
          meta: { menuPath: 'productAnalyze' },
          path: 'productAnalyze',
          component: () =>
            import('@/views/ko/analyze/productAnalyze/ProductAnalyze.vue'),
          beforeEnter: checkPermission,
        },
        /*알람분석*/
        {
          meta: { menuPath: 'notificationAnalyze' },
          path: 'notificationAnalyze',
          component: () =>
            import(
              '@/views/ko/analyze/notificationAnalyze/NotificationAnalyze.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*서비스분석*/
        {
          meta: { menuPath: 'serviceAnalyze' },
          path: 'serviceAnalyze',
          component: () =>
            import('@/views/ko/analyze/serviceAnalyze/ServiceAnalyze.vue'),
          beforeEnter: checkPermission,
        },
        /*설치분석*/
        {
          meta: { menuPath: 'installationAnalyze' },
          path: 'installationAnalyze',
          component: () =>
            import(
              '@/views/ko/analyze/installationAnalyze/InstallationAnalyze.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*모니터링분석*/
        {
          meta: { menuPath: 'monitoringAnalyze' },
          path: 'monitoringAnalyze',
          component: () =>
            import(
              '@/views/ko/analyze/monitoringAnalyze/MonitoringAnalyze.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*주요부품분석*/
        {
          meta: { menuPath: 'mainPartsAnalyze' },
          path: 'mainPartsAnalyze',
          component: () =>
            import('@/views/ko/analyze/mainPartsAnalyze/MainPartsAnalyze.vue'),
          beforeEnter: checkPermission,
        },
        /*에너지사용량분석*/
        {
          meta: { menuPath: 'energyUsageAnalyze' },
          path: 'energyUsageAnalyze',
          component: () =>
            import(
              '@/views/ko/analyze/energyUsageAnalyze/EnergyUsageAnalyze.vue'
            ),
          beforeEnter: checkPermission,
        },

        /*기본*/
        /*제품정보*/
        {
          meta: { menuPath: 'productInfo' },
          path: 'productInfo',
          component: () =>
            import('@/views/ko/basic/product/productInfo/ProductInfo.vue'),
          beforeEnter: checkPermission,
        },
        /*제품상세*/
        {
          meta: { menuPath: 'productInfo' },
          path: 'productInfoDetail',
          component: () =>
            import(
              '@/views/ko/basic/product/productInfo/ProductInfoDetail.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*부품정보*/
        {
          meta: { menuPath: 'partsInfo' },
          path: 'partsInfo',
          component: () =>
            import('@/views/ko/basic/product/partsInfo/PartsInfo.vue'),
          beforeEnter: checkPermission,
        },
        /*서비스업체정보*/
        {
          meta: { menuPath: 'serviceCompanyInfo' },
          path: 'serviceCompanyInfo',
          component: () =>
            import(
              '@/views/ko/basic/company/serviceCompanyInfo/ServiceCompanyInfo.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*설치업체정보*/
        {
          meta: { menuPath: 'installerInfo' },
          path: 'installerInfo',
          component: () =>
            import('@/views/ko/basic/company/installerInfo/InstallerInfo.vue'),
          beforeEnter: checkPermission,
        },
        /*고객정보*/
        {
          meta: { menuPath: 'customerInfo' },
          path: 'customerInfo',
          component: () =>
            import('@/views/ko/basic/customer/customerInfo/CustomerInfo.vue'),
          beforeEnter: checkPermission,
        },
        /*설치실명제등록*/
        {
          meta: { menuPath: 'installationRealNameSystem' },
          path: 'installationRealNameSystem',
          component: () =>
            import(
              '@/views/ko/basic/installation/installationRealNameSystem/InstallationRealNameSystem.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*설치정보*/
        {
          meta: { menuPath: 'installationInfo' },
          path: 'installationInfo',
          component: () =>
            import(
              '@/views/ko/basic/installation/installationInfo/InstallationInfo.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*설치실명제 목록*/
        {
          meta: { menuPath: 'installationList' },
          path: 'installationList',
          component: () =>
            import(
              '@/views/ko/basic/installation/installationRealNameSystem/InstallationList.vue'
            ),
          beforeEnter: checkPermission,
        },

        /*지원센터*/
        /*제품별계산*/
        {
          meta: { menuPath: 'productCalc' },
          path: 'productCalc',
          component: () =>
            import(
              '@/views/ko/supportCenter/salesSupport/productCalc/ProductCalc.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*에너지사용량계산*/
        {
          meta: { menuPath: 'energyUsageCalc' },
          path: 'energyUsageCalc',
          component: () =>
            import(
              '@/views/ko/supportCenter/salesSupport/energyUsageCalc/EnergyUsageCalc.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*제품선정*/
        {
          meta: { menuPath: 'productSelection' },
          path: 'productSelection',
          component: () =>
            import(
              '@/views/ko/supportCenter/salesSupport/productSelection/ProductSelection.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*완료매장*/
        {
          meta: { menuPath: 'completedStore' },
          path: 'completedStore',
          component: () =>
            import(
              '@/views/ko/supportCenter/salesSupport/completedStore/CompletedStore.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*제품카달로그*/
        {
          meta: { menuPath: 'productCatalog' },
          path: 'productCatalog',
          component: () =>
            import(
              '@/views/ko/supportCenter/marketingSupport/productCatalog/ProductCatalog.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*교육자료*/
        {
          meta: { menuPath: 'eduData' },
          path: 'eduData',
          component: () =>
            import(
              '@/views/ko/supportCenter/marketingSupport/eduData/EduData.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*인증서&증명서*/
        {
          meta: { menuPath: 'certificate' },
          path: 'certificate',
          component: () =>
            import(
              '@/views/ko/supportCenter/marketingSupport/certificate/Certificate.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*메뉴얼*/
        {
          meta: { menuPath: 'manual' },
          path: 'manual',
          component: () =>
            import(
              '@/views/ko/supportCenter/marketingSupport/manual/Manual.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*고장진단가이드*/
        {
          meta: { menuPath: 'troubleShootingGuide' },
          path: 'troubleShootingGuide',
          component: () =>
            import(
              '@/views/ko/supportCenter/serviceSupport/troubleShootingGuide/TroubleshootingGuide.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*일반공지*/
        {
          meta: { menuPath: 'generalNotice' },
          path: 'generalNotice',
          component: () =>
            import(
              '@/views/ko/supportCenter/community/generalNotice/GeneralNotice.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*일반공지 글로벌*/
        {
          meta: { menuPath: 'generalNoticeGlobal' },
          path: 'generalNoticeGlobal',
          component: () =>
            import(
              '@/views/ko/supportCenter/community/generalNotice/GeneralNoticeGlobal.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*SQUARE SR*/
        {
          meta: { menuPath: 'serviceRequest' },
          path: 'serviceRequest',
          component: () =>
            import(
              '@/views/ko/supportCenter/community/serviceRequest/serviceRequest.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*기술공지*/
        {
          meta: { menuPath: 'techNotice' },
          path: 'techNotice',
          component: () =>
            import(
              '@/views/ko/supportCenter/community/techNotice/TechNotice.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*기술공유방*/
        {
          meta: { menuPath: 'techShare' },
          path: 'techShare',
          component: () =>
            import(
              '@/views/ko/supportCenter/community/techShare/TechShare.vue'
            ),
          beforeEnter: checkPermission,
        },

        /*설정*/
        /*등록가이드*/
        {
          meta: { menuPath: 'regGuide' },
          path: 'regGuide',
          component: () =>
            import('@/views/ko/setting/productReg/regGuide/RegGuide.vue'),
          beforeEnter: checkPermission,
        },
        /*제품연결 */
        {
          meta: { menuPath: 'productConnect' },
          path: 'productConnect',
          component: () =>
            import(
              '@/views/ko/setting/productReg/productConnect/ProductConnect.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*펌웨어관리*/
        {
          meta: { menuPath: 'firmwareMng' },
          path: 'firmwareMng',
          component: () =>
            import('@/views/ko/setting/firmware/firmwareMng/FirmwareMng.vue'),
          beforeEnter: checkPermission,
        },
        /*펌웨어버전관리*/
        {
          meta: { menuPath: 'firmwareVersionMng' },
          path: 'firmwareVersionMng',
          component: () =>
            import(
              '@/views/ko/setting/firmware/firmwareVersionMng/FirmwareVersionMng.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*사용자로그정보*/
        {
          meta: { menuPath: 'userLogInfo' },
          path: 'userLogInfo',
          component: () =>
            import('@/views/ko/setting/logInfo/userLogInfo/UserLogInfo.vue'),
          beforeEnter: checkPermission,
        },
        /*제품로그정보*/
        {
          meta: { menuPath: 'productLogInfo' },
          path: 'productLogInfo',
          component: () =>
            import(
              '@/views/ko/setting/logInfo/productLogInfo/ProductLogInfo.vue'
            ),
          beforeEnter: checkPermission,
        },

        /*매장모니터링*/
        /*운전정보*/
        {
          meta: { menuPath: 'operationInfo' },
          path: 'operationInfo',
          component: () =>
            import('@/views/ko/shopMonitoring/operationInfo/OperationInfo.vue'),
          beforeEnter: checkPermission,
        },
        /*envelop분석*/
        {
          meta: { menuPath: 'envelopAnalyze' },
          path: 'envelopAnalyze',
          component: () =>
            import(
              '@/views/ko/shopMonitoring/envelopAnalyze/EnvelopAnalyze.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*원격제어*/
        {
          meta: { menuPath: 'remoteControlMonitoring' },
          path: 'remoteControlMonitoring',
          component: () =>
            import(
              '@/views/ko/shopMonitoring/remoteControl/RemoteControlMonitoring.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*분석그래프*/
        {
          meta: { menuPath: 'analysisGraph' },
          path: 'analysisGraph',
          component: () =>
            import('@/views/ko/shopMonitoring/analysisGraph/AnalysisGraph.vue'),
          beforeEnter: checkPermission,
        },
        /*알람정보*/
        {
          meta: { menuPath: 'notificationInfo' },
          path: 'notificationInfo',
          component: () =>
            import(
              '@/views/ko/shopMonitoring/notificationInfo/NotificationInfo.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*로그정보*/
        {
          meta: { menuPath: 'logInfo' },
          path: 'logInfo',
          component: () =>
            import('@/views/ko/shopMonitoring/logInfo/LogInfo.vue'),
          beforeEnter: checkPermission,
        },

        /*중대형 마력 운전정보*/
        {
          meta: { menuPath: 'largePowerOperationInfo' },
          path: 'largePowerOperationInfo',
          component: () =>
            import(
              '@/views/ko/shopMonitoring/largePower/largePowerOperationInfo/LargePowerOperationInfo.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*envelop분석*/
        {
          meta: { menuPath: 'largePowerEnvelopAnalyze' },
          path: 'largePowerEnvelopAnalyze',
          component: () =>
            import(
              '@/views/ko/shopMonitoring/largePower/largePowerEnvelopAnalyze/LargePowerEnvelopAnalyze.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*원격제어*/
        {
          meta: { menuPath: 'largePowerRemoteControlMonitoring' },
          path: 'largePowerRemoteControlMonitoring',
          component: () =>
            import(
              '@/views/ko/shopMonitoring/largePower/largePowerRemoteControl/LargePowerRemoteControlMonitoring.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*분석그래프*/
        {
          meta: { menuPath: 'largePowerAnalysisGraph' },
          path: 'largePowerAnalysisGraph',
          component: () =>
            import(
              '@/views/ko/shopMonitoring/largePower/largePowerAnalysisGraph/LargePowerAnalysisGraph.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*알람정보*/
        {
          meta: { menuPath: 'largePowerNotificationInfo' },
          path: 'largePowerNotificationInfo',
          component: () =>
            import(
              '@/views/ko/shopMonitoring/largePower/largePowerNotificationInfo/LargePowerNotificationInfo.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*로그정보*/
        {
          meta: { menuPath: 'largePowerLogInfo' },
          path: 'largePowerLogInfo',
          component: () =>
            import(
              '@/views/ko/shopMonitoring/largePower/largePowerLogInfo/LargePowerLogInfo.vue'
            ),
          beforeEnter: checkPermission,
        },

        /* 샘플파일 업로드 */
        {
          meta: { menuPath: 'sampleFileUpload' },
          path: 'sampleFileUpload',
          component: () => import('@/views/ko/sample/SampleFileUpload.vue'),
          beforeEnter: permitPermission,
        },
        {
          meta: { menuPath: 'FileUpload' },
          path: 'FileUpload',
          component: () => import('@/views/ko/sample/FileUpload.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'FileDownload' },
          path: 'FileDownload',
          component: () => import('@/views/ko/sample/FileDownload.vue'),
          beforeEnter: checkPermission,
        },

        /* IoT Core 샘플 */
        {
          meta: { menuPath: 'IoTCorePubSub' },
          path: 'IoTCorePubSub',
          component: () => import('@/views/ko/sample/IoTCorePubSub.vue'),
          beforeEnter: checkPermission,
        },

        /** */
        {
          meta: { menuPath: 'storeLayout' },
          path: 'storeLayout',
          component: () =>
            import('@/views/ko/storeDashBoard/storeLayout/StoreLayout.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'storeLayout' },
          path: 'storeLayoutList',
          component: () =>
            import('@/views/ko/storeDashBoard/storeLayout/StoreLayout.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'storeLayout' },
          path: 'storeLayoutSetting',
          component: () =>
            import(
              '@/views/ko/storeDashBoard/storeLayoutSetting/StoreLayoutSetting.vue'
            ),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'konvaDemo' },
          path: 'konvaDemo',
          component: () => import('@/views/ko/sample/KonvaSample.vue'),
          beforeEnter: checkPermission,
        },

        /** 매장 레이아웃 */
        {
          meta: { menuPath: 'permissionTest' },
          path: 'permissionTest',
          component: () => import('@/views/ko/sample/PermissionTest.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'sampleMultiLang' },
          path: 'sampleMultiLang',
          component: () => import('@/views/ko/sample/MultiLang.vue'),
        },

        // 로딩바 샘플
        {
          meta: { menuPath: 'loadingTest' },
          path: 'loadingTest',
          component: () => import('@/views/ko/sample/LoadingBarTest.vue'),
          beforeEnter: permitPermission,
        },
        {
          meta: { menuPath: 'excelSample' },
          path: 'excelSample',
          component: () => import('@/views/ko/sample/ExcelExportSample.vue'),
          beforeEnter: permitPermission,
        },
      ],
      // alias :["/ko",'/en'],
      beforeEach: checkPermission,
    },
    // 다국어(영어) 시작
    {
      path: '/en/',
      redirect: store.state.login?.defaultUrl || 'login',
      component: () => import('@/views/ko/DefaultLayout.vue'),
      children: [
        // 로그인 처리된 레이아웃 적용 할 라우터들은 children에 추가
        {
          meta: { menuPath: 'mapboxTest' },
          path: 'mapboxTest',
          component: () => import('@/views/en/mapboxTest/MapboxTest.vue'),
          beforeEnter: permitPermission,
        },

        /* 관리자 */
        /* 사용자정보 (한국) */
        {
          meta: { menuPath: 'userMng' },
          path: 'userMng',
          component: () => import('@/views/ko/userMng/UserMng.vue'),
          beforeEnter: checkPermission,
        },
        /** 메뉴설정 (한국) */
        {
          meta: { menuPath: 'menuMng' },
          path: 'menuMng',
          component: () => import('@/views/ko/menuMng/MenuMng.vue'),
          beforeEnter: checkPermission,
        },
        /* 권한별 메뉴관리 (한국) */
        {
          meta: { menuPath: 'siteGroupMng' },
          path: 'siteGroupMng',
          component: () => import('@/views/ko/siteGroupMng/SiteGroupMng.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'myInfoMng' },
          path: 'myInfoMng',
          component: () => import('@/views/en/myInfo/MyInfoMng.vue'),
          beforeEnter: permitPermission,
        },

        /* 딜러용 대시보드 */
        {
          meta: { menuPath: 'dealDashBoard' },
          path: 'dealDashBoard',
          component: () => import('@/views/en/dash/DealDashBoard.vue'),
          beforeEnter: checkPermission,
        },

        /** 연구소 대시보드 (한국) */
        {
          meta: { menuPath: 'labDashBoard' },
          path: 'labDashBoard',
          component: () => import('@/views/ko/lab/LabDashBoard.vue'),
          beforeEnter: checkPermission,
        },

        /*알람 내역*/
        {
          meta: { menuPath: 'notificationList' },
          path: 'notificationList',
          component: () =>
            import('@/views/en/notification/NotificationList.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'notificationList' },
          path: 'notiSummary',
          component: () =>
            import('@/views/en/notification/details/AlarmDetails.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'notiSettings' },
          path: 'notiSettings',
          component: () => import('@/views/en/notification/NotiSettings.vue'),
          beforeEnter: checkPermission,
        },
        // {
        //   meta: { menuPath: 'notification' },
        //   path: 'notificationDetail',
        //   name: 'notificationDetail',
        //   component: () => import('@/views/en/notification/NotificationDetail.vue'),
        //   beforeEnter: checkPermission,
        // },

        {
          meta: { menuPath: 'notificationList' },
          path: 'notiSettings',
          name: 'notiSettings',
          component: () => import('@/views/en/notification/NotiSettings.vue'),
          beforeEnter: checkPermission,
        },
        // 서비스 홈 (한국)
        {
          meta: { menuPath: 'serviceDashBoard' },
          path: 'serviceDashBoard',
          component: () =>
            import('@/views/ko/serviceDashBoard/ServiceDashBoard.vue'),
          beforeEnter: checkPermission,
        },

        // 20240701 모니터링 router 추가 dj
        {
          meta: { menuPath: 'monitoringRegion' },
          path: 'monitoringRegion',
          component: () =>
            import('@/views/en/monitoring/region/MonitoringRegion.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'monitoringShop' },
          path: 'monitoringShop',
          component: () =>
            import('@/views/en/monitoring/store/MonitoringStore.vue'),
          beforeEnter: checkPermission,
        },

        /*원격제어*/
        {
          meta: { menuPath: 'remoteControl' },
          path: 'remoteControl',
          component: () => import('@/views/en/remotecontrol/RemoteControl.vue'),
          beforeEnter: checkPermission,
        },

        /*서비스*/
        /*AS접수*/
        {
          meta: { menuPath: 'asReceipt' },
          path: 'asReceipt',
          component: () =>
            import(
              '@/views/en/service/asGeneraInquire/asReceipt/AsReceipt.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*AS접수상세내역*/
        {
          meta: { menuPath: 'asReceipt' },
          path: 'asReceiptDetail',
          component: () =>
            import(
              '@/views/en/service/asGeneraInquire/asReceipt/AsReceiptDetail.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*일반문의*/
        {
          meta: { menuPath: 'generalInquire' },
          path: 'generalInquire',
          component: () =>
            import(
              '@/views/en/service/asGeneraInquire/generalInquire/GeneralInquire.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*서비스내역*/
        {
          meta: { menuPath: 'serviceHistory' },
          path: 'serviceHistory',
          component: () =>
            import(
              '@/views/en/service/serviceManage/serviceHistory/ServiceHistory.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*서비스진행/처리정보*/
        {
          meta: { menuPath: 'serviceHistory' },
          path: 'serviceHistoryDetail',
          component: () =>
            import(
              '@/views/en/service/serviceManage/serviceHistory/ServiceHistoryDetail.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*처리가이드검색*/
        {
          meta: { menuPath: 'processGuide' },
          path: 'processGuide',
          component: () =>
            import(
              '@/views/en/service/processGuide/processGuideSearch/ProcessGuide.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*부품재고현황 (한국)*/
        {
          meta: { menuPath: 'partsInv' },
          path: 'partsInv',
          component: () =>
            import('@/views/ko/service/partsInfo/partsInv/PartsInv.vue'),
          beforeEnter: checkPermission,
        },
        /*부품주문내역 (한국)*/
        {
          meta: { menuPath: 'partsOrder' },
          path: 'partsOrder',
          component: () =>
            import('@/views/ko/service/partsInfo/partsOrder/PartsOrder.vue'),
          beforeEnter: checkPermission,
        },

        /*분석*/
        /*제품분석 (한국)*/
        {
          meta: { menuPath: 'productAnalyze' },
          path: 'productAnalyze',
          component: () =>
            import('@/views/ko/analyze/productAnalyze/ProductAnalyze.vue'),
          beforeEnter: checkPermission,
        },
        /*알람분석*/
        {
          meta: { menuPath: 'notificationAnalyze' },
          path: 'notificationAnalyze',
          component: () =>
            import(
              '@/views/en/analyze/notificationAnalyze/NotificationAnalyze.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*서비스분석 (한국)*/
        {
          meta: { menuPath: 'serviceAnalyze' },
          path: 'serviceAnalyze',
          component: () =>
            import('@/views/ko/analyze/serviceAnalyze/ServiceAnalyze.vue'),
          beforeEnter: checkPermission,
        },
        /*설치분석*/
        {
          meta: { menuPath: 'installationAnalyze' },
          path: 'installationAnalyze',
          component: () =>
            import(
              '@/views/en/analyze/installationAnalyze/InstallationAnalyze.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*모니터링분석*/
        {
          meta: { menuPath: 'monitoringAnalyze' },
          path: 'monitoringAnalyze',
          component: () =>
            import(
              '@/views/en/analyze/monitoringAnalyze/MonitoringAnalyze.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*주요부품분석*/
        {
          meta: { menuPath: 'mainPartsAnalyze' },
          path: 'mainPartsAnalyze',
          component: () =>
            import('@/views/en/analyze/mainPartsAnalyze/MainPartsAnalyze.vue'),
          beforeEnter: checkPermission,
        },
        /*에너지사용량분석*/
        {
          meta: { menuPath: 'energyUsageAnalyze' },
          path: 'energyUsageAnalyze',
          component: () =>
            import(
              '@/views/en/analyze/energyUsageAnalyze/EnergyUsageAnalyze.vue'
            ),
          beforeEnter: checkPermission,
        },

        /*기본*/
        /*제품정보*/
        {
          meta: { menuPath: 'productInfo' },
          path: 'productInfo',
          component: () =>
            import('@/views/en/basic/product/productInfo/ProductInfo.vue'),
          beforeEnter: checkPermission,
        },
        /*제품상세*/
        {
          meta: { menuPath: 'productInfo' },
          path: 'productInfoDetail',
          component: () =>
            import(
              '@/views/en/basic/product/productInfo/ProductInfoDetail.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*부품정보 (영어)*/
        {
          meta: { menuPath: 'partsInfo' },
          path: 'partsInfo',
          component: () =>
            import('@/views/en/basic/product/partsInfo/PartsInfo.vue'),
          beforeEnter: checkPermission,
        },
        /*서비스업체정보*/
        {
          meta: { menuPath: 'serviceCompanyInfo' },
          path: 'serviceCompanyInfo',
          component: () =>
            import(
              '@/views/en/basic/company/serviceCompanyInfo/ServiceCompanyInfo.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*설치업체정보*/
        {
          meta: { menuPath: 'installerInfo' },
          path: 'installerInfo',
          component: () =>
            import('@/views/en/basic/company/installerInfo/InstallerInfo.vue'),
          beforeEnter: checkPermission,
        },
        /*고객정보*/
        {
          meta: { menuPath: 'customerInfo' },
          path: 'customerInfo',
          component: () =>
            import('@/views/en/basic/customer/customerInfo/CustomerInfo.vue'),
          beforeEnter: checkPermission,
        },
        /*설치실명제등록*/
        {
          meta: { menuPath: 'installationRealNameSystem' },
          path: 'installationRealNameSystem',
          component: () =>
            import(
              '@/views/en/basic/installation/installationRealNameSystem/InstallationRealNameSystem.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*설치정보*/
        {
          meta: { menuPath: 'installationInfo' },
          path: 'installationInfo',
          component: () =>
            import(
              '@/views/en/basic/installation/installationInfo/InstallationInfo.vue'
            ),
          beforeEnter: checkPermission,
        },

        /*지원센터*/
        /*제품별계산*/
        {
          meta: { menuPath: 'productCalc' },
          path: 'productCalc',
          component: () =>
            import(
              '@/views/en/supportCenter/salesSupport/productCalc/ProductCalc.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*에너지사용량계산 (한국)*/
        {
          meta: { menuPath: 'energyUsageCalc' },
          path: 'energyUsageCalc',
          component: () =>
            import(
              '@/views/ko/supportCenter/salesSupport/energyUsageCalc/EnergyUsageCalc.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*제품선정*/
        {
          meta: { menuPath: 'productSelection' },
          path: 'productSelection',
          component: () =>
            import(
              '@/views/en/supportCenter/salesSupport/productSelection/ProductSelection.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*제품카달로그*/
        {
          meta: { menuPath: 'productCatalog' },
          path: 'productCatalog',
          component: () =>
            import(
              '@/views/en/supportCenter/marketingSupport/productCatalog/ProductCatalog.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*교육자료*/
        {
          meta: { menuPath: 'eduData' },
          path: 'eduData',
          component: () =>
            import(
              '@/views/en/supportCenter/marketingSupport/eduData/EduData.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*인증서&증명서*/
        {
          meta: { menuPath: 'certificate' },
          path: 'certificate',
          component: () =>
            import(
              '@/views/en/supportCenter/marketingSupport/certificate/Certificate.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*메뉴얼*/
        {
          meta: { menuPath: 'manual' },
          path: 'manual',
          component: () =>
            import(
              '@/views/en/supportCenter/marketingSupport/manual/Manual.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*고장진단가이드*/
        {
          meta: { menuPath: 'troubleShootingGuide' },
          path: 'troubleShootingGuide',
          component: () =>
            import(
              '@/views/en/supportCenter/serviceSupport/troubleShootingGuide/TroubleshootingGuide.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*일반공지*/
        {
          meta: { menuPath: 'generalNotice' },
          path: 'generalNotice',
          component: () =>
            import(
              '@/views/en/supportCenter/community/generalNotice/GeneralNotice.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*일반공지 글로벌*/
        {
          meta: { menuPath: 'generalNoticeGlobal' },
          path: 'generalNoticeGlobal',
          component: () =>
            import(
              '@/views/en/supportCenter/community/generalNotice/GeneralNoticeGlobal.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*기술공지 (한국)*/
        {
          meta: { menuPath: 'techNotice' },
          path: 'techNotice',
          component: () =>
            import(
              '@/views/ko/supportCenter/community/techNotice/TechNotice.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*기술공유방*/
        {
          meta: { menuPath: 'techShare' },
          path: 'techShare',
          component: () =>
            import(
              '@/views/en/supportCenter/community/techShare/TechShare.vue'
            ),
          beforeEnter: checkPermission,
        },

        /*설정*/
        /*등록가이드*/
        {
          meta: { menuPath: 'regGuide' },
          path: 'regGuide',
          component: () =>
            import('@/views/en/setting/productReg/regGuide/RegGuide.vue'),
          beforeEnter: checkPermission,
        },
        /*제품연결 */
        {
          meta: { menuPath: 'productConnect' },
          path: 'productConnect',
          component: () =>
            import(
              '@/views/en/setting/productReg/productConnect/ProductConnect.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*펌웨어관리 (한국)*/
        {
          meta: { menuPath: 'firmwareMng' },
          path: 'firmwareMng',
          component: () =>
            import('@/views/ko/setting/firmware/firmwareMng/FirmwareMng.vue'),
          beforeEnter: checkPermission,
        },
        /*펌웨어버전관리 (한국)*/
        {
          meta: { menuPath: 'firmwareVersionMng' },
          path: 'firmwareVersionMng',
          component: () =>
            import(
              '@/views/ko/setting/firmware/firmwareVersionMng/FirmwareVersionMng.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*사용자로그정보*/
        {
          meta: { menuPath: 'userLogInfo' },
          path: 'userLogInfo',
          component: () =>
            import('@/views/en/setting/logInfo/userLogInfo/UserLogInfo.vue'),
          beforeEnter: checkPermission,
        },
        /*제품로그정보*/
        {
          meta: { menuPath: 'productLogInfo' },
          path: 'productLogInfo',
          component: () =>
            import(
              '@/views/en/setting/logInfo/productLogInfo/ProductLogInfo.vue'
            ),
          beforeEnter: checkPermission,
        },

        /*매장모니터링*/
        /*운전정보 (한국)*/
        {
          meta: { menuPath: 'operationInfo' },
          path: 'operationInfo',
          component: () =>
            import('@/views/ko/shopMonitoring/operationInfo/OperationInfo.vue'),
          beforeEnter: checkPermission,
        },
        /*envelop분석 (한국)*/
        {
          meta: { menuPath: 'envelopAnalyze' },
          path: 'envelopAnalyze',
          component: () =>
            import(
              '@/views/ko/shopMonitoring/envelopAnalyze/EnvelopAnalyze.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*원격제어 (한국)*/
        {
          meta: { menuPath: 'remoteControlMonitoring' },
          path: 'remoteControlMonitoring',
          component: () =>
            import(
              '@/views/ko/shopMonitoring/remoteControl/RemoteControlMonitoring.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*분석그래프 (한국)*/
        {
          meta: { menuPath: 'analysisGraph' },
          path: 'analysisGraph',
          component: () =>
            import('@/views/ko/shopMonitoring/analysisGraph/AnalysisGraph.vue'),
          beforeEnter: checkPermission,
        },
        /*알람정보 (한국)*/
        {
          meta: { menuPath: 'notificationInfo' },
          path: 'notificationInfo',
          component: () =>
            import(
              '@/views/ko/shopMonitoring/notificationInfo/NotificationInfo.vue'
            ),
          beforeEnter: checkPermission,
        },
        /*로그정보 (한국)*/
        {
          meta: { menuPath: 'logInfo' },
          path: 'logInfo',
          component: () =>
            import('@/views/ko/shopMonitoring/logInfo/LogInfo.vue'),
          beforeEnter: checkPermission,
        },

        /*중대형 마력 운전정보 (한국)*/
        {
          meta: { menuPath: 'largePowerOperationInfo' },
          path: 'largePowerOperationInfo',
          component: () =>
            import(
              '@/views/ko/shopMonitoring/largePower/largePowerOperationInfo/LargePowerOperationInfo.vue'
            ),
          beforeEnter: checkPermission,
        },

        /** */
        {
          meta: { menuPath: 'storeLayout' },
          path: 'storeLayout',
          component: () =>
            import('@/views/en/storeDashBoard/storeLayout/StoreLayout.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'storeLayout' },
          path: 'storeLayoutList',
          component: () =>
            import('@/views/en/storeDashBoard/storeLayout/StoreLayout.vue'),
          beforeEnter: checkPermission,
        },
        {
          meta: { menuPath: 'storeLayout' },
          path: 'storeLayoutSetting',
          component: () =>
            import(
              '@/views/en/storeDashBoard/storeLayoutSetting/StoreLayoutSetting.vue'
            ),
          beforeEnter: checkPermission,
        },

        {
          meta: { menuPath: 'test' },
          path: 'tempGooglePostCode',
          component: () =>
            import('@/components/en/address/TempGooglePostcode.vue'),
          //beforeEnter: checkPermission,
        },
      ],
      beforeEach: checkPermission,
    },

    // 다국어(영어) 끝
    // 비로그인 레이아웃 적용할 라우터추가
    {
      path: '/login',
      component: () => import('@/views/login/LoginPage.vue'),
      beforeEnter: loginPermission,
    },
    {
      path: '/loginExplore',
      component: () => import('@/views/login/LoginPageExplore.vue'),
      beforeEnter: loginPermission,
    },
    // 비회원 as 접수확인 리스트
    {
      path: '/ko/',
      component: () => import('@/views/ko/GuestLayout.vue'),
      children: [
        {
          meta: { menuPath: 'notAuthAsReceiptCheck' },
          path: 'notAuthAsReceiptCheck',
          component: () =>
            import(
              '@/views/ko/service/asGeneraInquire/asReceipt/NotAuthAsReceiptCheck.vue'
            ),
          beforeEnter: guestAccessOnly,
        },
        {
          meta: { menuPath: 'notAuthAsReceiptDetail' },
          path: 'notAuthAsReceiptDetail',
          component: () =>
            import(
              '@/views/ko/service/asGeneraInquire/asReceipt/NotAuthAsReceiptDetail.vue'
            ),
          beforeEnter: guestAccessOnly,
        },
      ],
    },
    // 비회원 as 접수확인 리스트 - 다국어(영어)
    {
      path: '/en/',
      component: () => import('@/views/en/GuestLayout.vue'),
      children: [
        {
          meta: { menuPath: 'notAuthAsReceiptCheck' },
          path: 'notAuthAsReceiptCheck',
          component: () =>
            import(
              '@/views/en/service/asGeneraInquire/asReceipt/NotAuthAsReceiptCheck.vue'
            ),
          beforeEnter: guestAccessOnly,
        },
        {
          meta: { menuPath: 'notAuthAsReceiptDetail' },
          path: 'notAuthAsReceiptDetail',
          component: () =>
            import(
              '@/views/en/service/asGeneraInquire/asReceipt/NotAuthAsReceiptDetail.vue'
            ),
          beforeEnter: guestAccessOnly,
        },
      ],
    },
    // 배치도 새창띄우기
    {
      meta: { menuPath: 'storeLayout' },
      path: '/ko/storeLayoutNewBatch',
      component: () =>
        import('@/views/ko/storeDashBoard/storeLayout/StoreLayoutNewBatch.vue'),
      beforeEnter: checkPermission,
    },
    {
      meta: { menuPath: 'storeLayout' },
      path: '/en/storeLayoutNewBatch',
      component: () =>
        import('@/views/en/storeDashBoard/storeLayout/StoreLayoutNewBatch.vue'),
      beforeEnter: checkPermission,
    },

    // pdf 뷰어 새창띄우기
    {
      meta: { menuPath: 'troubleShootingGuide' },
      path: '/ko/pdfView',
      component: () => import('@/views/ko/popup/PopupViewerPdf.vue'),
      beforeEnter: checkPermission,
    },
    {
      meta: { menuPath: 'troubleShootingGuide' },
      path: '/ko/imgView',
      component: () => import('@/views/ko/popup/PopupViewerImg.vue'),
      beforeEnter: checkPermission,
    },
    {
      meta: { menuPath: 'troubleShootingGuide' },
      path: '/en/pdfView',
      component: () => import('@/views/en/popup/PopupViewerPdf.vue'),
      beforeEnter: checkPermission,
    },
    {
      meta: { menuPath: 'troubleShootingGuide' },
      path: '/en/imgView',
      component: () => import('@/views/en/popup/PopupViewerImg.vue'),
      beforeEnter: checkPermission,
    },

    {
      meta: { menuPath: 'termsPrivacyPolicy' },
      path: '/ko/termsPrivacyPolicy',
      component: () => import('@/views/ko/myInfo/TermsPrivacyPolicy.vue'),
      beforeEnter: permitPermission,
    },

    {
      meta: { menuPath: 'termsPrivacyPolicy' },
      path: '/en/termsPrivacyPolicy',
      component: () => import('@/views/en/myInfo/TermsPrivacyPolicy.vue'),
      beforeEnter: permitPermission,
    },

    {
      meta: { menuPath: 'termsMembershipService' },
      path: '/ko/termsMembershipService',
      component: () => import('@/views/ko/myInfo/TermsMembershipService.vue'),
      beforeEnter: permitPermission,
    },

    // 영어도 해야함 - 20250623 ssl
    // {
    // 	meta: { menuPath: 'termsMembershipService' },
    // 	path: '/en/termsMembershipService',
    // 	component: () => import('@/views/en/myInfo/TermsMembershipService.vue'),
    // 	beforeEnter: permitPermission,
    // },

    {
      meta: { menuPath: 'termsSquareService' },
      path: '/ko/termsSquareService',
      component: () => import('@/views/ko/myInfo/TermsSquareService.vue'),
      beforeEnter: permitPermission,
    },

    // 영어도 해야함 - 20250623 ssl
    // {
    // 	meta: { menuPath: 'termsSquareService' },
    // 	path: '/en/termsSquareService',
    // 	component: () => import('@/views/en/myInfo/TermsSquareService.vue'),
    // 	beforeEnter: permitPermission,
    // },

    // 페이지 못찾을경우 에러페이지로 리다이렉트
    {
      path: '/ko/notFound',
      component: () => import('@/views/ko/error/ErrorPage404.vue'),
    },
    {
      path: '/ko/forbidden',
      component: () => import('@/views/ko/error/ErrorPage403.vue'),
    },
    {
      path: '/en/notFound',
      component: () => import('@/views/en/error/ErrorPage404.vue'),
    },
    {
      path: '/en/forbidden',
      component: () => import('@/views/en/error/ErrorPage403.vue'),
    },
    { path: '/ko/:pathMatch(.*)*', redirect: '/notFound' },
    { path: '/en/:pathMatch(.*)*', redirect: '/notFound' },
    { path: '/:pathMatch(.*)*', redirect: localeCheck },
  ],
});

// router.beforeEach((to, from, next) => {
//   console.log('page :', to.path); // 이동할 페이지의 이름을 콘솔에 출력
//   next(); // 페이지 이동을 계속 진행
// });

export default router;
